import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Switch } from '../ui/switch';

const CommunityPageManager = () => {
  const [settings, setSettings] = useState({
    title: 'Join the Community',
    subtitle: '',
    description: '',
    image_url: '/community-journey.png',
    enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/community-page`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setPreviewUrl(data.image_url);
      }
    } catch (error) {
      console.error('Error fetching community page settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Please select a valid image file (JPG, PNG, GIF, WEBP)' });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size must be less than 5MB' });
        return;
      }

      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      
      setMessage(null);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please select an image first' });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_BASE}/api/upload/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update settings with new image URL
        const newSettings = { ...settings, image_url: data.url };
        setSettings(newSettings);
        
        // Force preview URL update
        setPreviewUrl(data.url);
        setSelectedFile(null);
        
        setMessage({ type: 'success', text: `Image uploaded successfully! URL: ${data.url}. Click "Save Settings" to apply.` });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to upload image' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error uploading image' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/community-page`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Community page settings saved successfully!' });
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to save settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p>Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="text-2xl">👥</span>
          Community Page Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {/* Page Enabled Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div>
            <Label className="text-base font-semibold">Enable Community Page</Label>
            <p className="text-sm text-gray-600 dark:text-gray-400">Show or hide the community page from users</p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
          />
        </div>

        {/* Title */}
        <div>
          <Label htmlFor="title">Page Title</Label>
          <Input
            id="title"
            value={settings.title}
            onChange={(e) => setSettings({ ...settings, title: e.target.value })}
            placeholder="Join the Community"
            className="mt-2"
          />
        </div>

        {/* Subtitle */}
        <div>
          <Label htmlFor="subtitle">Subtitle (Optional)</Label>
          <Input
            id="subtitle"
            value={settings.subtitle || ''}
            onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
            placeholder="Connect with fitness enthusiasts"
            className="mt-2"
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={settings.description || ''}
            onChange={(e) => setSettings({ ...settings, description: e.target.value })}
            placeholder="Write a compelling description for your community..."
            rows={4}
            className="mt-2"
          />
        </div>

        {/* Image Upload */}
        <div className="border-2 border dark:border-gray-700-red-200 rounded-lg p-6 bg-red-50/50">
          <Label className="text-lg font-semibold mb-3 block">Community Image</Label>
          
          {/* File Input */}
          <div className="mb-4">
            <Input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="border-2 border dark:border-gray-700-purple-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-purple-700"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              📸 Supported formats: JPG, PNG, GIF, WEBP (Max 5MB)
            </p>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <div className="mb-4 p-3 bg-white dark:bg-gray-800 border border dark:border-gray-700-red-200 rounded-lg">
              <p className="text-sm font-semibold text-red-700">Selected File:</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">📄 {selectedFile.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Size: {(selectedFile.size / 1024).toFixed(2)} KB</p>
            </div>
          )}

          {/* Upload Button */}
          {selectedFile && (
            <Button
              onClick={handleUploadImage}
              disabled={uploading}
              className="w-full mb-4 bg-red-600 hover:bg-red-700 text-white"
            >
              {uploading ? (
                <>
                  <span className="mr-2">⏳</span> Uploading...
                </>
              ) : (
                <>
                  <span className="mr-2">📤</span> Upload Image
                </>
              )}
            </Button>
          )}
          
          {/* Image Preview */}
          {previewUrl && (
            <div className="mt-4 border-2 border-dashed border dark:border-gray-700-purple-300 rounded-lg p-4 bg-white dark:bg-gray-800">
              <p className="text-sm font-semibold mb-3 text-red-700">Preview:</p>
              <img
                key={previewUrl}
                src={previewUrl}
                alt="Preview"
                className="max-w-full h-auto rounded-lg shadow-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Current URL Display (Read-only) */}
          <div className="mt-4 p-3 bg-gray-100 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Current Image URL:</p>
            <p className="text-sm text-gray-800 font-mono break-all">{settings.image_url || 'No image set'}</p>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 text-white font-semibold py-3"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Preview Link */}
        <div className="text-center">
          <a
            href="/community"
            target="_blank"
            rel="noopener noreferrer"
            className="text-red-600 hover:text-red-700 font-semibold underline"
          >
            → Preview Community Page
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommunityPageManager;
